import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { useT } from '../i18n';
import { usePopoverClose } from '../lib/hooks';

export interface SelectOption<V extends string = string> {
  value: V;
  label: ReactNode;
  /** Plain text for search / type-ahead (defaults to the label when it is a string). */
  text?: string;
  /** Secondary text on the right (e.g. "205°", "3 robots"). */
  meta?: string;
  group?: string;
  disabled?: boolean;
}

interface Props<V extends string> {
  value: V;
  onChange: (v: V) => void;
  options: SelectOption<V>[];
  ariaLabel?: string;
  placeholder?: ReactNode;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** Classes / style for the wrapper (layout: grow, flex basis…). */
  className?: string;
  style?: CSSProperties;
  /** Extra classes for the trigger button. */
  triggerClassName?: string;
  /** Force the search box on/off (default: on when more than 8 options). */
  searchable?: boolean;
  id?: string;
}

const textOf = <V extends string>(o: SelectOption<V>) => o.text ?? (typeof o.label === 'string' ? o.label : String(o.value));

/**
 * App-wide dropdown replacing native <select> (which scrolls badly with long lists on some browsers):
 * button trigger + floating popover (fixed, flips/caps to the viewport), search when > 8 options,
 * keyboard (↑/↓/Home/End/Enter/Esc, type-ahead), groups and disabled options.
 */
export function Select<V extends string>({ value, onChange, options, ariaLabel, placeholder, disabled, size = 'md', className, style, triggerClassName, searchable, id }: Props<V>) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(-1);
  const closedAt = useRef(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const typeBuf = useRef({ s: '', at: 0 });
  const listId = useId();
  const close = (focusTrigger = true) => {
    setOpen(false);
    setQ('');
    closedAt.current = Date.now();
    if (focusTrigger) triggerRef.current?.focus();
  };
  const popRef = usePopoverClose(open, () => close(false));
  const withSearch = searchable ?? options.length > 8;

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => textOf(o).toLowerCase().includes(needle) || String(o.value).toLowerCase().includes(needle)) : options;
  }, [options, q]);
  const current = options.find((o) => o.value === value);

  const openList = () => {
    if (disabled) return;
    // The popover is positioned from the trigger's rect: make sure the trigger is on screen first.
    triggerRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    setOpen(true);
    setActive(Math.max(0, shown.findIndex((o) => o.value === value)));
  };

  // Focus the search box / list and keep the active option visible.
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => (withSearch ? searchRef.current : listRef.current)?.focus(), 0);
    return () => clearTimeout(id);
  }, [open, withSearch]);
  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const pick = (o: SelectOption<V> | undefined) => {
    if (!o || o.disabled) return;
    if (o.value !== value) onChange(o.value);
    close();
  };
  const move = (dir: 1 | -1, from = active) => {
    if (!shown.length) return;
    let i = from;
    for (let n = 0; n < shown.length; n++) {
      i = (i + dir + shown.length) % shown.length;
      if (!shown[i]!.disabled) break;
    }
    setActive(i);
  };

  const onListKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      move(1, -1);
    } else if (e.key === 'End') {
      e.preventDefault();
      move(-1, shown.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(shown[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab') {
      close(false);
    } else if (!withSearch && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      // type-ahead
      const now = Date.now();
      const buf = now - typeBuf.current.at > 600 ? e.key : typeBuf.current.s + e.key;
      typeBuf.current = { s: buf, at: now };
      const i = shown.findIndex((o) => !o.disabled && textOf(o).toLowerCase().startsWith(buf.toLowerCase()));
      if (i >= 0) setActive(i);
    }
  };
  const onTriggerKey = (e: KeyboardEvent) => {
    if (open) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openList();
    }
  };

  const headers = shown.map((o, i) => (o.group && o.group !== shown[i - 1]?.group ? o.group : null));
  return (
    <div className={`pop-anchor sel-wrap${className ? ` ${className}` : ''}`} style={style}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={`select sel-trigger${size === 'sm' ? ' sm' : ''}${triggerClassName ? ` ${triggerClassName}` : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        title={current ? textOf(current) : undefined}
        disabled={disabled}
        onClick={() => {
          if (open) close();
          else if (Date.now() - closedAt.current > 250) openList();
        }}
        onKeyDown={onTriggerKey}
      >
        <span className="sel-value">{current ? current.label : (placeholder ?? (value ? String(value) : '—'))}</span>
        <svg className="sel-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div ref={popRef} className="popover sel-pop" style={{ left: 0, right: 0, top: 36 }} onKeyDown={onListKey}>
          {withSearch && (
            <input
              ref={searchRef}
              className="input sm"
              type="search"
              placeholder={t('select.search')}
              aria-label={t('select.search')}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setActive(0);
              }}
            />
          )}
          <div ref={listRef} id={listId} className="sel-list" role="listbox" tabIndex={-1} aria-label={ariaLabel} aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}>
            {shown.map((o, i) => {
              const header = headers[i];
              return (
                <div key={`${o.value}-${i}`} style={{ display: 'contents' }}>
                  {header && <div className="sel-group">{header}</div>}
                  <div
                    id={`${listId}-${i}`}
                    data-idx={i}
                    role="option"
                    aria-selected={o.value === value}
                    aria-disabled={o.disabled || undefined}
                    className={`sel-opt${o.value === value ? ' sel' : ''}${i === active ? ' active' : ''}${o.disabled ? ' disabled' : ''}`}
                    title={textOf(o)}
                    onMouseEnter={() => !o.disabled && setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(o)}
                  >
                    <span className="sel-opt-label">{o.label}</span>
                    {o.meta && <span className="sel-opt-meta">{o.meta}</span>}
                  </div>
                </div>
              );
            })}
            {!shown.length && <div className="muted" style={{ padding: 8, fontSize: 13 }}>{t('select.noMatches')}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
